import { useCallback } from "react";
import { generatePath, useNavigate, useOutletContext, useParams } from "react-router";

import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { MapButtonFitBounds } from "~/modules/map/components/MapButtonFitBounds";
import { MapButtonToggleBaseMap } from "~/modules/map/components/MapButtonToggleBaseMap";
import { MapPanel } from "~/modules/map/components/MapPanel";
import { GaiaMapExpandableMarkerAreaOnMap } from "~/modules/plannerV2/components/GaiaExpandableMapMarkerArea/GaiaMapMarkerAreaOnMap";
import { AreasLayerV2 } from "~/modules/plannerV2/components/map-layers/AreasLayerV2";
import { DraggablePickupPointLayer } from "~/modules/plannerV2/components/map-layers/DraggablePickupPointLayer";
import { FieldworkerHomeMarkersLayer } from "~/modules/plannerV2/components/map-layers/FieldworkerHomeMarkersLayer";
import RoutesLayerV2 from "~/modules/plannerV2/components/map-layers/RoutesLayerV2";
import { PLANNER_MAP_PADDING } from "~/modules/plannerV2/constants/map";
import usePlannerV2TravelGroupGetTimeline, {
    AreasWithAdditionalVisits,
} from "~/modules/plannerV2/screens/PlannerV2Screen/PlannerV2RouteScreen/hooks/usePlannerV2TravelGroupGetTimeline";
import { createSearchParamsString } from "~/shared/helpers/createSearchParamsString";

import { PlannerV2RouteFilters } from "../PlannerV2VisitDetailScreen/components/PlannerV2RouteFilters";
import { usePlannerV2VisitFilters } from "../PlannerV2VisitDetailScreen/hooks/usePlannerV2VisitFilters";
import { PlannerV2ContextType } from "../types";
import { calculateRouteScreenBounds } from "./calculateBounds";
import { RouteDetailPanelHeader } from "./components/RouteDetailPanelHeader";
import { RouteDetailPanelTimeline } from "./components/RouteDetailPanelTimeline/";
import { usePlannerV2RouteAreaActions } from "./hooks/usePlannerV2RouteAreaActions";
import { usePlannerV2RoutePickupActions } from "./hooks/usePlannerV2RoutePickupActions";

export function PlannerV2RouteScreen() {
    const { date, plannerVisitId, plannerTravelGroupId } = useParams();

    if (!date || !plannerVisitId || !plannerTravelGroupId) {
        throw new Error("Parameters `date`, `plannerVisitId`, or `plannerTravelGroupId` are missing");
    }

    const { plannerSharedLeftPanel, plannerSharedBottomRightPanel } = useOutletContext<PlannerV2ContextType>();
    const navigate = useNavigate();
    const { filterValues } = usePlannerV2VisitFilters();

    const backHref = {
        pathname: "..",
        search: createSearchParamsString({
            filterFieldworkerIncludeUnavailable: String(filterValues.filterFieldworkerIncludeUnavailable),
        }),
    };

    const handleOnSuccess = useCallback(
        (nextPlannerTravelGroupId: string) => {
            if (nextPlannerTravelGroupId === plannerTravelGroupId) return;

            navigate(
                generatePath("../travel-groups/:plannerTravelGroupId", {
                    plannerTravelGroupId: nextPlannerTravelGroupId,
                }),
                { replace: true },
            );
        },
        [navigate, plannerTravelGroupId],
    );

    const {
        data: {
            travelGroupMeta,
            timeline,
            areasWithAdditionalVisits,
            areasWithAdditionalVisitsById,
            areasFromTimelineWithNoAdditionalVisits,
            areasFromTimelineWithNoAdditionalVisitsById,
            routeTravels,
            pickupPoints,
            fieldworkerHomeMarkers,
        },
    } = usePlannerV2TravelGroupGetTimeline({ onDate: date, travelGroupId: plannerTravelGroupId });

    const { handleWorkerDragged, removePickupPoint, loadingToRemoveShiftId, pendingPickupAction } =
        usePlannerV2RoutePickupActions({ travelGroupId: plannerTravelGroupId, onSuccess: handleOnSuccess });

    const { addVisitToTravelGroup, removeVisitFromTravelGroup, loading, error, loadingToRemoveVisitId } =
        usePlannerV2RouteAreaActions({
            travelGroupId: plannerTravelGroupId,
            allowIfUnavailable: filterValues.filterFieldworkerIncludeUnavailable,
            onSuccess: handleOnSuccess,
        });

    const bounds = calculateRouteScreenBounds(
        [...areasWithAdditionalVisits, ...areasFromTimelineWithNoAdditionalVisits],
        pickupPoints,
    );

    return (
        travelGroupMeta && (
            <>
                <GaiaMapUIOverlay
                    leftPanel={
                        <>
                            {plannerSharedLeftPanel}
                            <PlannerV2RouteFilters />
                        </>
                    }
                    bottomRightPanel={plannerSharedBottomRightPanel}
                    rightPanel={
                        <MapPanel>
                            <RouteDetailPanelHeader {...travelGroupMeta} backHref={backHref} />

                            {timeline && (
                                <RouteDetailPanelTimeline
                                    ownerFieldworkerId={travelGroupMeta.plannerTravelGroup.ownerPlannerShift?.fieldworker.id}
                                    loadingToRemoveShiftId={loadingToRemoveShiftId}
                                    loadingToRemoveVisitId={loadingToRemoveVisitId}
                                    onRemovePickupPointClick={async shiftId => {
                                        await removePickupPoint({
                                            plannerShiftId: shiftId,
                                            plannerTravelGroupId: plannerTravelGroupId,
                                        });
                                    }}
                                    visitClickHref={(plannerAreaId, plannerVisitId) =>
                                        generatePath("/planner/:date/areas/:plannerAreaId/visits/:plannerVisitId", {
                                            date,
                                            plannerAreaId,
                                            plannerVisitId,
                                        })
                                    }
                                    areaNameHref={plannerAreaId =>
                                        generatePath("/planner/:date/areas/:plannerAreaId", {
                                            date,
                                            plannerAreaId,
                                        })
                                    }
                                    onRemoveVisitClick={removeVisitFromTravelGroup}
                                    timeline={timeline}
                                />
                            )}
                        </MapPanel>
                    }
                    bottomBar={
                        <>
                            <MapButtonFitBounds bounds={bounds} padding={PLANNER_MAP_PADDING} />
                            <MapButtonToggleBaseMap />
                        </>
                    }
                />

                {/* render areas that have additional visits */}
                {areasWithAdditionalVisits && (
                    <AreasLayerV2<AreasWithAdditionalVisits>
                        layerId="areas-with-additional-visits"
                        areas={areasWithAdditionalVisits}
                        areasById={areasWithAdditionalVisitsById}
                        renderHoveredMarker={(element, hideMarker) => (
                            <GaiaMapExpandableMarkerAreaOnMap
                                areaNameHref={plannerAreaId =>
                                    generatePath("/planner/:date/areas/:plannerAreaId", { date, plannerAreaId })
                                }
                                area={element.plannerArea}
                                coordinates={element.location.coordinates}
                                onMouseLeave={hideMarker}
                                possibleAdjacentVisits={element.combinable}
                                onVisitAdd={async (timeslotId, visitId) => {
                                    await addVisitToTravelGroup(timeslotId, visitId);
                                    hideMarker();
                                }}
                                disabled={loading}
                                error={error}
                            />
                        )}
                    />
                )}

                {/* render route endpoints with minimal hover */}
                {areasFromTimelineWithNoAdditionalVisits && (
                    <AreasLayerV2
                        layerId="areas-with-no-additional-visits"
                        areas={areasFromTimelineWithNoAdditionalVisits}
                        areasById={areasFromTimelineWithNoAdditionalVisitsById}
                    />
                )}

                <FieldworkerHomeMarkersLayer points={fieldworkerHomeMarkers} />
                <RoutesLayerV2 routes={routeTravels} />
                <DraggablePickupPointLayer
                    points={pickupPoints}
                    ownerTravelMethod={travelGroupMeta.plannerTravelGroup.ownerBillingType}
                    onPickupDrag={handleWorkerDragged}
                    pendingPickupAction={pendingPickupAction}
                />
            </>
        )
    );
}
